import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Space,
  Modal,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  ReloadOutlined,
  EnvironmentOutlined,
  CheckOutlined,
  StarOutlined,
  LockOutlined,
} from '@ant-design/icons';
import {
  fetchDeliveryAddressesRequest,
  setDeliveryAddressEditing,
  clearDeliveryAddressEditing
} from '../../../redux/actions/deliveryAddressActions';
import { hasPermission } from '../../../utils/permissions';
import DeliveryAddressForm from './DeliveryAddressForm';
import './DeliveryAddress.css';

const { Search } = Input;
const { Option } = Select;

const DeliveryAddress = () => {
  const dispatch = useDispatch();
  const { addresses, loading, totalCount } = useSelector(state => state.deliveryAddresses);
  const permissions = useSelector(state => state.auth.permissions || []);
  
  // Check permissions
  const canViewGrid = hasPermission(permissions, 'delivery_address/gridView');
  const canCreate = hasPermission(permissions, 'delivery_address/create');
  const canEdit = hasPermission(permissions, 'delivery_address/edit');
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [filters, setFilters] = useState({
    is_active: undefined,
    is_default: undefined,
    city: '',
    state: '',
    pincode: ''
  });

  useEffect(() => {
    if (canViewGrid) {
      dispatch(fetchDeliveryAddressesRequest(filters));
    }
  }, [dispatch, filters, canViewGrid]);

  const handleAddNew = () => {
    dispatch(clearDeliveryAddressEditing());
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    dispatch(setDeliveryAddressEditing(record));
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    dispatch(clearDeliveryAddressEditing());
  };

  const handleRefresh = () => {
    dispatch(fetchDeliveryAddressesRequest(filters));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      is_active: undefined,
      is_default: undefined,
      city: '',
      state: '',
      pincode: ''
    });
  };

  // If user doesn't have permission to view grid, show message
  if (!canViewGrid) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon"><LockOutlined /></div>
        <div className="empty-state-text">
          You don't have permission to view delivery addresses.
        </div>
      </div>
    );
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Unit Name',
      dataIndex: 'unit_name',
      key: 'unit_name',
      width: 150,
      ellipsis: {
        showTitle: false,
      },
      render: (unit_name) => (
        <Tooltip placement="topLeft" title={unit_name}>
          {unit_name || '-'}
        </Tooltip>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      ellipsis: {
        showTitle: false,
      },
      render: (address) => (
        <Tooltip placement="topLeft" title={address}>
          {address}
        </Tooltip>
      ),
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
      width: 120,
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: 120,
    },
    {
      title: 'Pincode',
      dataIndex: 'pincode',
      key: 'pincode',
      width: 100,
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 100,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      render: (phone) => (
        phone ? phone : '-'
      ),
    },
    {
      title: 'Default',
      dataIndex: 'is_default',
      key: 'is_default',
      width: 80,
      render: (is_default) => (
        <Tag color={is_default ? 'green' : 'default'}>
          {is_default ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (is_active) => (
        <Tag color={is_active ? 'success' : 'error'}>
          {is_active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    ...(canEdit ? [{
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
        </Space>
      ),
    }] : []),
  ];

  return (
    <div style={{ padding: '1rem' }}>
      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon"><EnvironmentOutlined /></div>
          <div className="stat-content">
            <div className="stat-value">{(addresses || []).length}</div>
            <div className="stat-label">Total Addresses</div>
          </div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#28a745' }}>
          <div className="stat-icon" style={{ background: '#d4edda', color: '#155724' }}><CheckOutlined /></div>
          <div className="stat-content">
            <div className="stat-value">{(addresses || []).filter(a => a.is_active).length}</div>
            <div className="stat-label">Active</div>
          </div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#ffc107' }}>
          <div className="stat-icon" style={{ background: '#fff3cd', color: '#856404' }}><StarOutlined /></div>
          <div className="stat-content">
            <div className="stat-value">{(addresses || []).filter(a => a.is_default).length}</div>
            <div className="stat-label">Default</div>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="modern-table-container" style={{ marginTop: '1.5rem' }}>
        <div className="table-header">
          <h2 className="table-title">Delivery Addresses</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={handleRefresh}>
              <ReloadOutlined /> Refresh
            </button>
            {canCreate && (
              <button className="btn btn-primary" onClick={handleAddNew}>
                <PlusOutlined /> Add New Address
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px', marginBottom: '1rem' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Search by city"
              value={filters.city}
              onChange={(e) => handleFilterChange('city', e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Search by state"
              value={filters.state}
              onChange={(e) => handleFilterChange('state', e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder="Pincode"
              value={filters.pincode}
              onChange={(e) => handleFilterChange('pincode', e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Status"
              value={filters.is_active}
              onChange={(value) => handleFilterChange('is_active', value)}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Space>
              <Button onClick={clearFilters}>
                Clear Filters
              </Button>
            </Space>
          </Col>
        </Row>
        </div>

        <Table
          columns={columns}
          dataSource={addresses}
          rowKey="id"
          loading={loading}
          pagination={{
            total: totalCount,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} addresses`,
          }}
          scroll={{ x: 1200 }}
          locale={{
            emptyText: (
              <div className="empty-state">
                <div className="empty-state-icon"><EnvironmentOutlined /></div>
                <div className="empty-state-text">No delivery addresses found</div>
                {canCreate && (
                  <button className="btn btn-primary" onClick={handleAddNew}>
                    Add Your First Address
                  </button>
                )}
              </div>
            ),
          }}
        />
      </div>

      <Modal
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={800}
        destroyOnClose
        centered
      >
        <DeliveryAddressForm onCancel={handleModalClose} />
      </Modal>
    </div>
  );
};

export default DeliveryAddress;